import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import useRequireLogin from '../../hooks/useRequireLogin';
import {
  getWorkshopBooking,
  cancelWorkshopBooking,
  rescheduleWorkshopBooking,
} from '../../services/workshopBookings';
import {
  sendWorkshopBookingMessage,
  MAX_MESSAGE_LENGTH,
} from '../../services/messageThreads';
import {
  resolveWorkshopBookingAccount,
  fetchWorkshopBookingSlots,
} from '../../services/workshopBookingReschedule';
import { formatEurosNoSpace } from '../../utils/utils.jsx';
import {
  blocsHorairesAvecComplet,
  heure,
  horaireLong,
  joursCombines,
  jourLong,
  majuscule,
  parJour,
} from '../../annuaire/tunnel/creneaux';
import { MAX_PROPOSED_SLOTS } from '../../annuaire/tunnel/bookingRequest';
import { Bloc, Complet, Grille } from '../../annuaire/tunnel/EtapeCreneaux';
import {
  workshopBookingStatusKey,
  workshopBookingStatusColor,
  isPendingWorkshopBooking,
  canCancelWorkshopBooking,
  canRescheduleWorkshopBooking,
  workshopBookingActionErrorKey,
} from '../../utils/workshopBookingStatus';
import { messageThreadActionErrorKey } from '../../utils/messageThreadStatus';
import useAlert from '../../hooks/useModal';
import AccountNav from '../../components/customerArea/shared/AccountNav';
import ProposedSlotsList from '../../components/customerArea/workshopBookings/ProposedSlotsList';
import Row from '../../components/shared/atoms/Row';
import Box from '../../components/shared/atoms/Box';
import Container from '../../components/shared/atoms/Container';
import Heading from '../../components/shared/atoms/Heading';
import Text from '../../components/shared/atoms/Text';
import Link from '../../components/shared/atoms/Link';
import Badge from '../../components/shared/atoms/Badge';
import Button from '../../components/shared/atoms/Button';
import Input from '../../components/shared/atoms/Input';

const ErrorBox = styled.div`
  padding: 14px;
  margin: 16px 0;
  background: ${(p) => p.theme.colors.red.pale};
  color: ${(p) => p.theme.colors.red.dark};
  border-radius: 8px;
  font-size: 14px;
`;

const SuccessBox = styled.div`
  padding: 14px;
  margin: 16px 0;
  background: ${(p) => p.theme.colors.green.pale};
  color: ${(p) => p.theme.colors.green.dark};
  border-radius: 8px;
  font-size: 14px;
`;

// api#1220 : champ additif, null ou absent hors demande en attente. Une
// réponse sans créneau exploitable est traitée comme absente.
const pendingRescheduleRequest = (booking) => {
  const request = booking && booking.reschedule_request;
  const first = request && request.slots && request.slots[0];
  return first && first.starts_at ? request : null;
};

// FIX-1155 : une demande porte de 1 à 10 horaires, déjà triés par l'api —
// affichés dans l'ordre reçu.
const requestedStarts = (request) =>
  request.slots
    .filter((slot) => slot && slot.starts_at)
    .map((s) => s.starts_at);

const StyledBox = styled(Box)`
  border: 1px solid ${(p) => p.theme.colors.gray.light};
`;

const SlotButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  margin: 0 8px 8px 0;
  border-radius: 8px;
  border: 1.5px solid ${(p) => p.theme.colors.blue.electric};
  background: ${(p) =>
    p.disabled ? p.theme.colors.gray.lightest : p.theme.colors.white};
  color: ${(p) => p.theme.colors.blue.electric};
  font-weight: 600;
  font-size: 14px;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
`;

// Le panneau Déplacer liste ses jours d'un bloc (pas de bande de pastilles
// jour par jour façon tunnel annuaire, cf. rapport § « bilan ») : sur les
// ~21 jours de fenêtre (MAX_SPAN_DAYS), tout afficher produisait une page
// de 7300 px à 390 px de large (QA du 24/09). N'afficher QUE les premiers
// jours qui ont des créneaux — jamais un jour vide, `parJour` ne groupe que
// ce qui en a — et « Voir d'autres dates » en ajoute ce pas à chaque clic.
const RESCHEDULE_DAYS_STEP = 5;

/**
 * FIXERLIGHT-052 lot 2 — détail d'un rendez-vous d'atelier, avec annuler et
 * déplacer (contrat § 5-6, api#882/#892). Aucun bouton sur un rendez-vous
 * `done`/`cancelled` ; sur une demande `requested`, seulement Annuler — le
 * mapping vit dans `canCancelWorkshopBooking`/`canRescheduleWorkshopBooking`
 * (`utils/workshopBookingStatus.js`), jamais un statut en dur ici.
 */
function WorkshopBookingDetail() {
  const { t, i18n } = useTranslation();
  const client = useSelector((s) => s.client);
  useRequireLogin();
  const { bid: bookingId } = useParams();
  const showAlert = useAlert();
  const history = useHistory();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Bandeau de résultat d'une action (annuler/déplacer) : succès ou échec,
  // jamais les deux à la fois — même geste que `ErrorBox` ci-dessus, pas de
  // toast (cet écran n'a pas `<ToastContainer />` dans son arbre, lui
  // seulement dans `Header.jsx`).
  const [actionMessage, setActionMessage] = useState(null); // { kind: 'success'|'error', text }
  const [actionBusy, setActionBusy] = useState(false);
  // `booking_too_close` (§ 7/§ 8) ne se résout jamais en attendant — le
  // temps ne fait que réduire encore le délai. Une fois vu, les deux
  // boutons restent désactivés plutôt que de laisser réessayer une action
  // vouée au même refus.
  const [actionLocked, setActionLocked] = useState(false);
  const [reschedule, setReschedule] = useState({
    open: false,
    loading: false,
    // 'unavailable' : la fiche de l'atelier n'est pas publiée, ou sa
    // résolution/ses disponibilités ont échoué — jamais une exception.
    error: null,
    shopId: null,
    instant: false,
    slots: [],
    // FIX-1155, atelier à confirmer : heures complètes (grisées, jamais
    // sélectionnables) et horaires cochés, au plus MAX_PROPOSED_SLOTS.
    fullSlots: [],
    selected: [],
    capReached: false,
    // Jours affichés (parmi ceux qui ont des créneaux) : la fenêtre de
    // disponibilité (MAX_SPAN_DAYS, 21 jours) rendait tout listé d'un bloc
    // — page de 7300 px à 390 px de large (QA du 24/09). « Voir d'autres
    // dates » en ajoute RESCHEDULE_DAYS_STEP à chaque clic.
    visibleDays: RESCHEDULE_DAYS_STEP,
  });

  // FIXERLIGHT-300 : « Écrire à l'atelier » depuis la fiche. Le texte saisi
  // survit à toute erreur ; seul un envoi réussi navigue vers le fil.
  const [write, setWrite] = useState({
    open: false,
    body: '',
    sending: false,
    error: null,
  });

  const submitWrite = async () => {
    if (!write.body.trim() || write.sending) return;
    setWrite((w) => ({ ...w, sending: true, error: null }));
    try {
      const thread = await sendWorkshopBookingMessage(
        client.id,
        bookingId,
        write.body,
      );
      history.push(`/messages/${thread.id}`);
    } catch (e) {
      setWrite((w) => ({
        ...w,
        sending: false,
        error: (e && e.kind) || 'generic',
      }));
    }
  };

  const loadBooking = async (isCancelled) => {
    setLoading(true);
    setError(false);
    try {
      const data = await getWorkshopBooking(client.id, bookingId);
      if (isCancelled()) return;
      setBooking(data);
    } catch (e) {
      if (isCancelled()) return;
      setError(true);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useEffect(() => {
    if (!client.id) return undefined;
    let cancelled = false;
    loadBooking(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, bookingId]);

  const statusKey = booking && workshopBookingStatusKey(booking.status);
  const pending = booking && isPendingWorkshopBooking(booking.status);

  const closeReschedulePanel = () =>
    setReschedule({
      open: false,
      loading: false,
      error: null,
      shopId: null,
      instant: false,
      slots: [],
      fullSlots: [],
      selected: [],
      capReached: false,
      visibleDays: RESCHEDULE_DAYS_STEP,
    });

  const submitCancel = async () => {
    setActionBusy(true);
    setActionMessage(null);
    try {
      const updated = await cancelWorkshopBooking(client.id, bookingId);
      setBooking(updated);
      closeReschedulePanel();
      setActionMessage({
        kind: 'success',
        text: t('workshopappointments.detail.cancel.success'),
      });
    } catch (e) {
      const code = e && e.code;
      setActionMessage({
        kind: 'error',
        text: t(workshopBookingActionErrorKey(code)),
      });
      if (code === 'booking_not_cancellable') {
        // Le statut réel a pu diverger (déjà annulé/terminé ailleurs) : on
        // resynchronise plutôt que de garder des boutons qui mentent, et un
        // panneau Déplacer ouvert n'a plus de sens sur ce nouvel état.
        closeReschedulePanel();
        loadBooking(() => false);
      } else if (code === 'booking_too_close') {
        // Ne se résout jamais en attendant : verrouille les deux actions,
        // et un panneau Déplacer ouvert proposerait des créneaux tout aussi
        // voués au même refus.
        closeReschedulePanel();
        setActionLocked(true);
      }
    } finally {
      setActionBusy(false);
    }
  };

  const openCancelConfirm = () => {
    showAlert({
      title: t('workshopappointments.detail.cancel.confirm.title'),
      content: t('workshopappointments.detail.cancel.confirm.text'),
      showModal: true,
      showButton: [
        { text: t('button.cancel'), variant: 'neutral' },
        {
          text: t('workshopappointments.detail.cancel.confirm.submit'),
          color: 'primary.darkest',
          textColor: 'white',
          datatestid: 'workshop-booking-cancel-confirm',
          onClick: submitCancel,
        },
      ],
    });
  };

  /**
   * Charge (ou recharge) les créneaux proposables pour `shopId`/`instant`
   * déjà connus, sans re-résoudre la fiche de l'atelier — utilisé à
   * l'ouverture du panneau ET pour rafraîchir la liste après un refus
   * `slot_not_available` (le créneau choisi vient d'être pris, le reste de
   * la liste peut encore contenir des places).
   */
  const loadRescheduleSlots = async (shopId, instant) => {
    setReschedule((r) => ({ ...r, loading: true, error: null }));
    try {
      const dispo = await fetchWorkshopBookingSlots(shopId, booking.requested);
      // L'api recalcule `duration_min` à partir des prestations ACTUELLES du
      // compte (`durationFor`) — pas de la durée réellement retenue sur ce
      // rendez-vous (`booking.duration_min`). Les deux divergent quand
      // l'atelier a modifié la durée du rendez-vous, ou changé/archivé une
      // prestation depuis : dans ce cas les créneaux rendus seraient refusés
      // par `/reschedule` (qui vérifie contre la durée réelle), donc on
      // affiche l'état indisponible plutôt que des créneaux voués à l'échec.
      const staleDuration =
        dispo &&
        dispo.duration_min != null &&
        booking.duration_min != null &&
        dispo.duration_min !== booking.duration_min;
      if (staleDuration) {
        setReschedule((r) => ({ ...r, loading: false, error: 'unavailable' }));
        return;
      }
      const slots = (dispo && dispo.slots) || [];
      setReschedule((r) => ({
        ...r,
        loading: false,
        shopId,
        instant,
        slots,
        fullSlots: (dispo && dispo.full_slots) || [],
        // Une liste rafraîchie (après slot_not_available) ne garde cochés
        // que les horaires encore proposables.
        selected: instant
          ? []
          : r.selected.filter((iso) => slots.includes(iso)),
        capReached: false,
        // Repart de la fenêtre par défaut : une liste rafraîchie (après
        // slot_not_available) peut avoir un tout autre premier jour.
        visibleDays: RESCHEDULE_DAYS_STEP,
      }));
    } catch (e) {
      setReschedule((r) => ({ ...r, loading: false, error: 'unavailable' }));
    }
  };

  // Résout la fiche de l'atelier (et donc son mode, instantané ou à
  // confirmer) puis charge ses créneaux, sans toucher au bandeau d'action.
  const loadRescheduleAccount = async () => {
    setReschedule({
      open: true,
      loading: true,
      error: null,
      shopId: null,
      instant: false,
      slots: [],
      fullSlots: [],
      selected: [],
      capReached: false,
      visibleDays: RESCHEDULE_DAYS_STEP,
    });
    const account = await resolveWorkshopBookingAccount(
      booking.shop && booking.shop.url,
    );
    if (!account) {
      setReschedule((r) => ({ ...r, loading: false, error: 'unavailable' }));
      return;
    }
    await loadRescheduleSlots(account.shopId, account.instant);
  };

  const openReschedulePanel = () => {
    setActionMessage(null);
    return loadRescheduleAccount();
  };

  /**
   * `slot` : un ISO (atelier instantané, `scheduled_at`) ou la liste des
   * horaires cochés (atelier à confirmer, `proposed_slots`, FIX-1155).
   */
  const submitReschedule = async (slot) => {
    setActionBusy(true);
    setActionMessage(null);
    try {
      const updated = await rescheduleWorkshopBooking(
        client.id,
        bookingId,
        slot,
      );
      setBooking(updated);
      closeReschedulePanel();
      const request = pendingRescheduleRequest(updated);
      const starts = request ? requestedStarts(request) : [];
      let text = t('workshopappointments.detail.reschedule.success');
      if (starts.length > 1) {
        text = t('workshopappointments.detail.reschedule.successPendingMany', {
          count: starts.length,
          current: majuscule(horaireLong(updated.scheduled_at)),
        });
      } else if (request) {
        text = t('workshopappointments.detail.reschedule.successPending', {
          requested: majuscule(horaireLong(starts[0])),
          current: majuscule(horaireLong(updated.scheduled_at)),
        });
      }
      setActionMessage({ kind: 'success', text });
    } catch (e) {
      const code = e && e.code;
      setActionMessage({
        kind: 'error',
        text: t(workshopBookingActionErrorKey(code)),
      });
      if (code === 'booking_not_confirmed') {
        // Le rendez-vous n'est plus `confirmed` (déjà changé ailleurs) : on
        // resynchronise et referme le panneau, ses créneaux ne valent plus
        // rien pour ce nouvel état.
        closeReschedulePanel();
        loadBooking(() => false);
      } else if (code === 'booking_too_close') {
        // Ne se résout jamais en attendant : verrouille les deux actions et
        // referme le panneau, dont les créneaux seraient tout aussi refusés.
        closeReschedulePanel();
        setActionLocked(true);
      } else if (code === 'slot_not_available') {
        // Le rendez-vous LUI reste `confirmed` : seul CE créneau est
        // périmé. On recharge la liste plutôt que de fermer le panneau ou
        // de laisser le créneau refusé cliquable.
        loadRescheduleSlots(reschedule.shopId, reschedule.instant);
      } else if (code === 'single_slot_required') {
        // L'atelier réserve désormais en direct : on relit son mode, le
        // panneau repasse au choix d'un seul horaire.
        loadRescheduleAccount();
      }
    } finally {
      setActionBusy(false);
    }
  };

  /**
   * `slot` : un ISO (atelier instantané) ou les horaires cochés, triés
   * (atelier à confirmer) — envoyé tel quel à `submitReschedule`.
   */
  const openRescheduleConfirm = (slot) => {
    const isos = Array.isArray(slot) ? slot : [slot];
    const many = isos.length > 1;
    showAlert({
      title: t('workshopappointments.detail.reschedule.confirm.title'),
      content: (
        <>
          {many ? (
            <Box mb={2}>
              <Text mb={1}>
                {t('workshopappointments.detail.reschedule.confirm.textMany')}
              </Text>
              {isos.map((iso) => (
                <Text key={iso} fontWeight={500}>
                  {majuscule(horaireLong(iso))}
                </Text>
              ))}
            </Box>
          ) : (
            <Text mb={reschedule.instant ? 0 : 2}>
              {t('workshopappointments.detail.reschedule.confirm.text')}{' '}
              {majuscule(horaireLong(isos[0]))}
            </Text>
          )}
          {!reschedule.instant && (
            <Text color="gray.dark" fontSize={1}>
              {t(
                many
                  ? 'workshopappointments.detail.reschedule.confirm.needsConfirmationMany'
                  : 'workshopappointments.detail.reschedule.confirm.needsConfirmation',
              )}
            </Text>
          )}
        </>
      ),
      showModal: true,
      showButton: [
        { text: t('button.cancel'), variant: 'neutral' },
        {
          text: t('workshopappointments.detail.reschedule.confirm.submit'),
          color: 'primary.darkest',
          textColor: 'white',
          datatestid: 'workshop-booking-reschedule-confirm',
          onClick: () => submitReschedule(slot),
        },
      ],
    });
  };

  // Atelier à confirmer : cocher / décocher un bloc d'une heure, au plus
  // MAX_PROPOSED_SLOTS — le plafond de la réservation, dit AVANT l'envoi.
  const toggleRescheduleSlot = (iso) =>
    setReschedule((r) => {
      if (r.selected.includes(iso)) {
        return {
          ...r,
          selected: r.selected.filter((s) => s !== iso),
          capReached: false,
        };
      }
      if (r.selected.length >= MAX_PROPOSED_SLOTS) {
        return { ...r, capReached: true };
      }
      return { ...r, selected: [...r.selected, iso], capReached: false };
    });

  const canCancel = booking && canCancelWorkshopBooking(booking.status);
  const canReschedule = booking && canRescheduleWorkshopBooking(booking.status);
  // Atelier instantané : un seul horaire, la liste des heures libres.
  // Atelier à confirmer (FIX-1155) : les blocs d'une heure de l'étape 2 de la
  // réservation, complet compris — seulement les jours qui ont au moins une
  // heure libre (jamais un jour entièrement grisé dans cette liste paginée).
  let jours = [];
  if (reschedule.open) {
    jours = reschedule.instant
      ? parJour(reschedule.slots)
      : joursCombines(reschedule.slots, reschedule.fullSlots).filter(
          (jour) => jour.creneaux.length,
        );
  }
  const joursVisibles = jours.slice(0, reschedule.visibleDays);
  const dAutresJours = jours.length > reschedule.visibleDays;

  return (
    <Container>
      <Container fluid pb={10} pt={4}>
        <Helmet
          title={t('workshopappointments.detail.meta.title')}
          meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
        />
        <Row md gap={56}>
          <AccountNav />
          <Box mt={{ xs: -7, lg: 0 }} style={{ flex: 1 }}>
            <Link
              to="/account/rendez-vous-atelier"
              color="black"
              mb={4}
              data-testid="workshop-booking-detail-back">
              &larr; {t('workshopappointments.detail.back')}
            </Link>

            {loading && (
              <Box mt={4} data-testid="workshop-booking-detail-skeleton">
                <Skeleton style={{ borderRadius: 8 }} height={220} />
              </Box>
            )}

            {!loading && error && (
              <ErrorBox data-testid="workshop-booking-detail-error">
                {t('workshopappointments.detail.error')}
              </ErrorBox>
            )}

            {!loading && !error && booking && (
              <Box mt={4}>
                <Box
                  flexDirection="row"
                  flexWrap="wrap"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ rowGap: 8 }}
                  mb={2}>
                  <Heading level={2}>
                    {booking.shop && booking.shop.name}
                  </Heading>
                  {statusKey && (
                    <Badge
                      color={workshopBookingStatusColor(booking.status)}
                      light>
                      {t(statusKey)}
                    </Badge>
                  )}
                </Box>

                {/* FIXERLIGHT-267 : le numéro rendu par l'api (« RDV-4821 »),
                    affiché tel quel ; absent → rien. */}
                {!!booking.reference && (
                  <Text
                    color="gray.dark"
                    mb={3}
                    data-testid="workshop-booking-detail-reference">
                    {t('workshopappointments.reference', {
                      reference: booking.reference,
                    })}
                  </Text>
                )}

                <Box mb={4}>
                  {pending ? (
                    <ProposedSlotsList slots={booking.proposed_slots} />
                  ) : (
                    <Text fontWeight={2} variant="large" color="black">
                      {majuscule(horaireLong(booking.scheduled_at))}
                    </Text>
                  )}
                </Box>

                {!!pendingRescheduleRequest(booking) && (
                  <StyledBox borderRadius="xs" p={4} mb={3}>
                    {requestedStarts(pendingRescheduleRequest(booking)).length >
                    1 ? (
                      <Box data-testid="workshop-booking-detail-reschedule-pending">
                        <Text mb={1}>
                          {t(
                            'workshopappointments.detail.reschedule.pendingMany',
                            {
                              current: majuscule(
                                horaireLong(booking.scheduled_at),
                              ),
                            },
                          )}
                        </Text>
                        {requestedStarts(pendingRescheduleRequest(booking)).map(
                          (iso) => (
                            <Text
                              key={iso}
                              fontWeight={500}
                              data-testid="workshop-booking-detail-reschedule-pending-slot">
                              {majuscule(horaireLong(iso))}
                            </Text>
                          ),
                        )}
                      </Box>
                    ) : (
                      <Text data-testid="workshop-booking-detail-reschedule-pending">
                        {t('workshopappointments.detail.reschedule.pending', {
                          requested: majuscule(
                            horaireLong(
                              pendingRescheduleRequest(booking).slots[0]
                                .starts_at,
                            ),
                          ),
                          current: majuscule(horaireLong(booking.scheduled_at)),
                        })}
                      </Text>
                    )}
                  </StyledBox>
                )}

                <StyledBox borderRadius="xs" p={4} mb={3}>
                  <Heading level={4} mb={2}>
                    {t('workshopappointments.detail.shop.title')}
                  </Heading>
                  <Text mb={1}>{booking.shop && booking.shop.name}</Text>
                  {booking.shop && booking.shop.phone && (
                    <Text
                      mb={1}
                      as="a"
                      href={`tel:${booking.shop.phone}`}
                      color="black"
                      data-testid="workshop-booking-detail-phone">
                      {t('phone')} : {booking.shop.phone}
                    </Text>
                  )}
                  {booking.shop && booking.shop.url && (
                    <Box mt={2}>
                      <Link
                        href={booking.shop.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.electric"
                        data-testid="workshop-booking-detail-shop-link">
                        {t('workshopappointments.detail.shop.link')}
                      </Link>
                    </Box>
                  )}
                </StyledBox>

                {!!booking.address && (
                  <StyledBox borderRadius="xs" p={4} mb={3}>
                    <Heading level={4} mb={2}>
                      {t('workshopappointments.detail.address.title')}
                    </Heading>
                    <Text>{booking.address}</Text>
                  </StyledBox>
                )}

                {!!(booking.requested && booking.requested.length) && (
                  <StyledBox borderRadius="xs" p={4} mb={3}>
                    <Heading level={4} mb={2}>
                      {t('workshopappointments.detail.requested.title')}
                    </Heading>
                    {booking.requested.map((item, index) => (
                      <Text key={item.service_key || index}>{item.label}</Text>
                    ))}
                  </StyledBox>
                )}

                {!!booking.client_message && (
                  <StyledBox borderRadius="xs" p={4} mb={3}>
                    <Heading level={4} mb={2}>
                      {t('workshopappointments.detail.clientMessage.title')}
                    </Heading>
                    {/* Le texte du client tel qu'il l'a écrit, retours à la
                        ligne compris (FIXERLIGHT-280). */}
                    <Text
                      style={{ whiteSpace: 'pre-line' }}
                      data-testid="workshop-booking-detail-client-message">
                      {booking.client_message}
                    </Text>
                  </StyledBox>
                )}

                {!!(booking.bikes && booking.bikes.length) && (
                  <StyledBox borderRadius="xs" p={4} mb={3}>
                    <Heading level={4} mb={2}>
                      {t('workshopappointments.detail.bikes.title')}
                    </Heading>
                    {booking.bikes.map((bike, index) => (
                      <Text key={bike.id || index} mb={1}>
                        {[bike.brand, bike.model, bike.type]
                          .filter(Boolean)
                          .join(' — ')}
                      </Text>
                    ))}
                  </StyledBox>
                )}

                {booking.travel_surcharge_eur != null && (
                  <StyledBox borderRadius="xs" p={4} mb={3}>
                    <Box flexDirection="row" justifyContent="space-between">
                      <Text fontWeight={500}>
                        {t('workshopappointments.detail.travelSurcharge.label')}
                      </Text>
                      <Text
                        fontWeight={2}
                        data-testid="workshop-booking-detail-surcharge">
                        {formatEurosNoSpace(
                          booking.travel_surcharge_eur,
                          i18n.language,
                        )}
                      </Text>
                    </Box>
                  </StyledBox>
                )}

                {actionMessage && actionMessage.kind === 'success' && (
                  <SuccessBox data-testid="workshop-booking-action-success">
                    {actionMessage.text}
                  </SuccessBox>
                )}
                {actionMessage && actionMessage.kind === 'error' && (
                  <ErrorBox data-testid="workshop-booking-action-error">
                    {actionMessage.text}
                  </ErrorBox>
                )}

                {(canCancel || canReschedule) && (
                  <Box
                    flexDirection="row"
                    flexWrap="wrap"
                    style={{ gap: 12 }}
                    mt={3}>
                    {canReschedule && (
                      <Button
                        variant="neutral"
                        size="compact"
                        disabled={actionBusy || actionLocked}
                        data-testid="workshop-booking-reschedule-button"
                        onClick={openReschedulePanel}>
                        {t('workshopappointments.detail.reschedule.button')}
                      </Button>
                    )}
                    {canCancel && (
                      <Button
                        variant="outlinedBlack"
                        size="compact"
                        disabled={actionBusy || actionLocked}
                        data-testid="workshop-booking-cancel-button"
                        onClick={openCancelConfirm}>
                        {t('workshopappointments.detail.cancel.button')}
                      </Button>
                    )}
                  </Box>
                )}

                <Box alignItems="flex-start" mt={3}>
                  {!write.open && (
                    <Button
                      variant="neutral"
                      size="compact"
                      data-testid="workshop-booking-write-button"
                      onClick={() => setWrite((w) => ({ ...w, open: true }))}>
                      {t('workshopappointments.detail.write.button')}
                    </Button>
                  )}
                </Box>

                {write.open && (
                  <StyledBox
                    borderRadius="xs"
                    p={4}
                    mt={3}
                    data-testid="workshop-booking-write-panel">
                    <Heading level={4} mb={2}>
                      {t('workshopappointments.detail.write.title')}
                    </Heading>
                    <Input
                      as="textarea"
                      rows={4}
                      name="workshop-booking-write"
                      bg="white"
                      placeholder={t(
                        'workshopappointments.detail.write.placeholder',
                      )}
                      value={write.body}
                      onChange={(e) => {
                        const value = e.target.value.slice(
                          0,
                          MAX_MESSAGE_LENGTH,
                        );
                        setWrite((w) => ({ ...w, body: value }));
                      }}
                      maxLength={MAX_MESSAGE_LENGTH}
                      data-testid="workshop-booking-write-input"
                    />
                    <Text
                      fontSize={0}
                      color="gray.dark"
                      textAlign="right"
                      mt={1}
                      data-testid="workshop-booking-write-counter">
                      {write.body.length}/{MAX_MESSAGE_LENGTH}
                    </Text>
                    {write.error && (
                      <ErrorBox data-testid="workshop-booking-write-error">
                        {t(messageThreadActionErrorKey(write.error))}
                      </ErrorBox>
                    )}
                    <Box
                      flexDirection="row"
                      flexWrap="wrap"
                      alignItems="center"
                      style={{ gap: 12 }}
                      mt={2}>
                      <Button
                        disabled={!write.body.trim() || write.sending}
                        onClick={submitWrite}
                        data-testid="workshop-booking-write-submit">
                        {t('workshopappointments.detail.write.submit')}
                      </Button>
                      <Button
                        variant="link"
                        size="compact"
                        disabled={write.sending}
                        onClick={() =>
                          setWrite({
                            open: false,
                            body: '',
                            sending: false,
                            error: null,
                          })
                        }
                        data-testid="workshop-booking-write-close">
                        {t('button.cancel')}
                      </Button>
                    </Box>
                  </StyledBox>
                )}

                {reschedule.open && (
                  <StyledBox
                    borderRadius="xs"
                    p={4}
                    mt={3}
                    data-testid="workshop-booking-reschedule-panel">
                    <Box
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}>
                      <Heading level={4}>
                        {t('workshopappointments.detail.reschedule.title')}
                      </Heading>
                      <Button
                        variant="link"
                        size="compact"
                        onClick={closeReschedulePanel}
                        data-testid="workshop-booking-reschedule-close">
                        {t('button.cancel')}
                      </Button>
                    </Box>

                    {reschedule.loading && (
                      <Box data-testid="workshop-booking-reschedule-loading">
                        <Skeleton style={{ borderRadius: 8 }} height={120} />
                      </Box>
                    )}

                    {!reschedule.loading && reschedule.error && (
                      <Text
                        color="gray.dark"
                        data-testid="workshop-booking-reschedule-unavailable">
                        {t(
                          'workshopappointments.detail.reschedule.unavailable',
                        )}
                      </Text>
                    )}

                    {!reschedule.loading &&
                      !reschedule.error &&
                      jours.length === 0 && (
                        <Text
                          color="gray.dark"
                          data-testid="workshop-booking-reschedule-no-slots">
                          {t('workshopappointments.detail.reschedule.noSlots')}
                        </Text>
                      )}

                    {!reschedule.loading &&
                      !reschedule.error &&
                      !reschedule.instant &&
                      jours.length > 0 && (
                        <Text color="gray.dark" mb={3}>
                          {t(
                            'workshopappointments.detail.reschedule.manyIntro',
                          )}
                        </Text>
                      )}

                    {!reschedule.loading &&
                      !reschedule.error &&
                      !reschedule.instant &&
                      joursVisibles.map((jour) => (
                        <Box key={jour.cle} mb={3}>
                          <Text fontWeight={500} mb={1}>
                            {majuscule(jourLong(jour.creneaux[0]))}
                          </Text>
                          <Grille>
                            {blocsHorairesAvecComplet(
                              jour.creneaux,
                              jour.creneauxComplets,
                            ).map((bloc) => {
                              if (bloc.complet) {
                                return (
                                  <Bloc
                                    key={bloc.heure}
                                    type="button"
                                    $complet
                                    disabled
                                    aria-label={t(
                                      'workshopappointments.detail.reschedule.fullAria',
                                      { block: bloc.libelle },
                                    )}
                                    data-testid={`workshop-booking-reschedule-full-${jour.cle}-${bloc.heure}`}>
                                    {bloc.libelle}
                                    <Complet>
                                      {t(
                                        'workshopappointments.detail.reschedule.full',
                                      )}
                                    </Complet>
                                  </Bloc>
                                );
                              }
                              const on = reschedule.selected.includes(bloc.iso);
                              return (
                                <Bloc
                                  key={bloc.heure}
                                  type="button"
                                  $on={on}
                                  aria-pressed={on}
                                  disabled={actionBusy}
                                  data-testid={`workshop-booking-reschedule-slot-${bloc.iso}`}
                                  onClick={() =>
                                    toggleRescheduleSlot(bloc.iso)
                                  }>
                                  {bloc.libelle}
                                  {on && ' ✓'}
                                </Bloc>
                              );
                            })}
                          </Grille>
                        </Box>
                      ))}

                    {!reschedule.loading &&
                      !reschedule.error &&
                      reschedule.instant &&
                      joursVisibles.map((jour) => (
                        <Box key={jour.cle} mb={3}>
                          <Text fontWeight={500} mb={1}>
                            {majuscule(jourLong(jour.creneaux[0]))}
                          </Text>
                          <Box flexDirection="row" flexWrap="wrap">
                            {jour.creneaux.map((iso) => (
                              <SlotButton
                                key={iso}
                                type="button"
                                disabled={actionBusy}
                                data-testid={`workshop-booking-reschedule-slot-${iso}`}
                                onClick={() => openRescheduleConfirm(iso)}>
                                {heure(iso)}
                              </SlotButton>
                            ))}
                          </Box>
                        </Box>
                      ))}

                    {!reschedule.loading && !reschedule.error && dAutresJours && (
                      <Button
                        variant="link"
                        size="compact"
                        disabled={actionBusy}
                        data-testid="workshop-booking-reschedule-more-days"
                        onClick={() =>
                          setReschedule((r) => ({
                            ...r,
                            visibleDays: r.visibleDays + RESCHEDULE_DAYS_STEP,
                          }))
                        }>
                        {t('workshopappointments.detail.reschedule.moreDays')}
                      </Button>
                    )}

                    {!reschedule.loading &&
                      !reschedule.error &&
                      !reschedule.instant &&
                      jours.length > 0 && (
                        <Box mt={3}>
                          {reschedule.capReached && (
                            <ErrorBox data-testid="workshop-booking-reschedule-cap">
                              {t('workshopappointments.detail.reschedule.cap', {
                                max: MAX_PROPOSED_SLOTS,
                              })}
                            </ErrorBox>
                          )}
                          <Text
                            mb={2}
                            aria-live="polite"
                            data-testid="workshop-booking-reschedule-count">
                            {reschedule.selected.length
                              ? t(
                                  'workshopappointments.detail.reschedule.selected',
                                  { count: reschedule.selected.length },
                                )
                              : t(
                                  'workshopappointments.detail.reschedule.selectedNone',
                                )}
                          </Text>
                          <Box alignItems="flex-start">
                            <Button
                              size="compact"
                              disabled={
                                actionBusy || reschedule.selected.length === 0
                              }
                              data-testid="workshop-booking-reschedule-submit"
                              onClick={() =>
                                openRescheduleConfirm(
                                  [...reschedule.selected].sort((a, b) =>
                                    a.localeCompare(b),
                                  ),
                                )
                              }>
                              {t(
                                'workshopappointments.detail.reschedule.submit',
                              )}
                            </Button>
                          </Box>
                        </Box>
                      )}
                  </StyledBox>
                )}
              </Box>
            )}
          </Box>
        </Row>
      </Container>
    </Container>
  );
}

export default WorkshopBookingDetail;
